# MVX Search API

Search CDC MVX codes for vaccine manufacturers.

## Endpoint

```
GET /v1/mvx/search
```

## Quick Start

<LanguageBlock>
  <Block language="node">
    ```typescript
    import { Fhirfly } from "@fhirfly-io/terminology";

    const client = new Fhirfly({ apiKey: "YOUR_API_KEY" });

    // Search for Pfizer
    const results = await client.mvx.search({
      q: "pfizer"
    });

    console.log(`Found ${results.total} manufacturers`);
    for (const item of results.items) {
      console.log(`${item.code}: ${item.display}`);
    }
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/mvx/search?q=pfizer"
    ```
  </Block>
</LanguageBlock>

## Parameters

### Text Search

| Parameter | Description | Example |
|-----------|-------------|---------|
| `q` | General text search | `q=moderna` |

### Filters

| Parameter | Type | Description | Example |
|-----------|------|-------------|---------|
| `status` | string | Manufacturer status | `active`, `inactive` |

### Pagination & Response

| Parameter | Default | Max | Description |
|-----------|---------|-----|-------------|
| `limit` | 20 | 100 | Results per page |
| `page` | 1 | 100 | Page number |
| `shape` | compact | — | `compact`, `standard`, `full` |

## Example Response

```json
{
  "facets": {
    "status": {
      "active": 2
    }
  },
  "has_more": false,
  "items": [
    {
      "code": "PFR",
      "display": "Pfizer, Inc",
      "status": "active"
    },
    {
      "code": "WAL",
      "display": "Wyeth",
      "status": "active"
    }
  ],
  "limit": 2,
  "meta": {
    "legal": {
      "attribution_required": false,
      "citation": "CDC National Center for Immunization and Respiratory Diseases. Accessed 2026-01-28 via FHIRfly.",
      "license": "public_domain",
      "source_name": "CDC Immunization"
    }
  },
  "page": 1,
  "total": 2,
  "total_capped": false
}
```

## Use Cases

### Find Major Vaccine Manufacturers

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.mvx.search({
      q: "moderna",
      status: "active"
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/mvx/search?q=moderna&status=active"
    ```
  </Block>
</LanguageBlock>

### Find All Active Manufacturers

<LanguageBlock>
  <Block language="node">
    ```typescript
    const results = await client.mvx.search({
      status: "active",
      limit: 100
    });
    ```
  </Block>
  <Block language="curl">
    ```bash
    curl -H "x-api-key: YOUR_API_KEY" \
      "https://api.fhirfly.io/v1/mvx/search?status=active&limit=100"
    ```
  </Block>
</LanguageBlock>

## Facets

MVX search returns these facets:

| Facet | Description |
|-------|-------------|
| `status` | Distribution by active/inactive |

## Common Manufacturer Codes

| Code | Manufacturer |
|------|--------------|
| `PFR` | Pfizer, Inc |
| `MOD` | Moderna US, Inc |
| `JSN` | Johnson & Johnson |
| `AZN` | AstraZeneca |
| `NVX` | Novavax |
| `MSD` | Merck Sharp & Dohme |
| `SKB` | GlaxoSmithKline |
| `WAL` | Wyeth (now Pfizer) |

## Valid Values

### Status Values

- `active` — Currently active manufacturer
- `inactive` — No longer active

## Required Scope

`mvx.search`

## See Also

- [MVX Lookup API](/docs/value-apis/mvx) — Look up a specific MVX code
- [CVX Search API](/docs/search-apis/cvx) — Search vaccine codes
- [Search APIs Overview](/docs/search-apis/overview) — Common search features
